import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SceneStore } from '@os/doc';
import type { MediaResolver, Variant } from '../editor/lod';
import type { Capabilities } from '../sharing/capabilities';
import { Button, Dialog, RadioGroup, Select } from '../../ui';
import { exportFileName } from './offline-copy';
import { sectionsInReadingOrder, type ExportTarget } from './plan';

/** What a board gives its exports: the file title, the image cap (§9.3) and where asset hashes and sizes come from. */
export type ExportSetup = {
  title: string;
  cap: Variant;
  protected: boolean;
  hashes(ids: string[]): Promise<Map<string, string>>;
  meta(id: string): { mime: string; w: number; h: number } | null;
};
export type ExportFormat = 'png' | 'jpeg' | 'pdf' | 'json' | 'osboard';
export type ExportRequest = {
  format: ExportFormat;
  scale: 1 | 2 | 4;
  target: ExportTarget;
  store: SceneStore;
  media: MediaResolver;
  setup: ExportSetup;
  signal: AbortSignal;
  onProgress(done: number, total: number): void;
};
export type ExportRun = (req: ExportRequest) => Promise<{ blob: Blob; name: string; missing: number; reducedTo: number | null }>;

const FORMATS: ExportFormat[] = ['png', 'jpeg', 'pdf', 'json', 'osboard'];
const SCALES = [1, 2, 4] as const;

/** No export entry point at all without `can_export`; a protected link exports images at 1024 px (spec §9.3). */
export function exporterFor(caps: Capabilities, base: Omit<ExportSetup, 'cap' | 'protected'>): ExportSetup | null {
  if (!caps.canExport) return null;
  return { ...base, cap: caps.protected ? 1024 : 2560, protected: caps.protected };
}

/** The export modules (snapdom, jsPDF, fflate) load on the first export only. */
export const defaultRun: ExportRun = async (req) => {
  const common = { store: req.store, media: req.media, cap: req.setup.cap, signal: req.signal, onProgress: req.onProgress };
  if (req.format === 'json' || req.format === 'osboard') {
    const { exportBoardFile } = await import('./board-export');
    const r = await exportBoardFile({ ...common, doc: req.store.doc, title: req.setup.title, kind: req.format, hashes: req.setup.hashes, meta: req.setup.meta });
    return { ...r, reducedTo: null };
  }
  const deps = await (await import('./capture')).browserDeps();
  if (req.format === 'pdf') {
    const r = await (await import('./pdf')).exportPdf({ ...common, deps });
    return { blob: r.blob, name: exportFileName(req.setup.title, 'pdf'), missing: r.missing, reducedTo: null };
  }
  const r = await (await import('./raster')).exportRaster({ ...common, deps, target: req.target, format: req.format, scale: req.scale });
  return { blob: r.blob, name: exportFileName(req.setup.title, req.format === 'jpeg' ? 'jpg' : 'png'), missing: r.missing, reducedTo: r.reduced ? r.scale : null };
};

export function saveBlob(name: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

const keyOf = (t: ExportTarget) => (t.kind === 'section' ? `section:${t.id}` : t.kind);

/**
 * Export dialog (spec §12): area (whole board, selection, one section) for images, PNG/JPEG at 1×/2×/4×, a PDF of the
 * sections, the board as JSON or `.osboard`; progress, cancel, and what the export had to give up.
 */
export function ExportDialog({
  store,
  media,
  setup,
  selection,
  initial,
  initialFormat = 'png',
  onClose,
  run = defaultRun,
  save = saveBlob,
}: {
  store: SceneStore;
  media: MediaResolver;
  setup: ExportSetup;
  selection: readonly string[];
  initial?: ExportTarget;
  /** The main menu's "Export as PDF"… (P5d). */
  initialFormat?: ExportFormat;
  onClose: () => void;
  run?: ExportRun;
  save?: (name: string, blob: Blob) => void;
}) {
  const { t } = useTranslation();
  const sections = useMemo(() => sectionsInReadingOrder(store), [store]);
  const [scope, setScope] = useState(keyOf(initial ?? (selection.length ? { kind: 'selection', ids: selection } : { kind: 'board' })));
  const [format, setFormat] = useState<ExportFormat>(initialFormat);
  const [scale, setScale] = useState<1 | 2 | 4>(2);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<[number, number] | null>(null);
  const [outcome, setOutcome] = useState<{ missing: number; reducedTo: number | null } | 'failed' | 'empty' | null>(null);
  const abort = useRef<AbortController | null>(null);
  const raster = format === 'png' || format === 'jpeg';

  const target = (): ExportTarget =>
    scope === 'selection' ? { kind: 'selection', ids: selection } : scope.startsWith('section:') ? { kind: 'section', id: scope.slice(8) } : { kind: 'board' };

  const start = async () => {
    const ctl = new AbortController();
    abort.current = ctl;
    setRunning(true);
    setProgress(null);
    setOutcome(null);
    try {
      const r = await run({ format, scale, target: target(), store, media, setup, signal: ctl.signal, onProgress: (d, n) => setProgress([d, n]) });
      save(r.name, r.blob);
      setOutcome({ missing: r.missing, reducedTo: r.reducedTo });
    } catch (err) {
      if (!(err instanceof DOMException && err.name === 'AbortError')) setOutcome((err as { code?: string }).code === 'empty' ? 'empty' : 'failed');
    } finally {
      abort.current = null;
      setRunning(false);
    }
  };

  return (
    <Dialog title={t('export.title')} onClose={() => (abort.current?.abort(), onClose())} size="md">
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!running) void start();
        }}
      >
        {raster && (
          <label className="flex flex-col gap-1 font-sans text-sm font-medium text-fg">
            {t('export.scope')}
            <Select value={scope} onChange={(e) => setScope(e.target.value)} disabled={running}>
              {/* With several pages (P14) a PNG is of the open page; the PDF holds every page. */}
              <option value="board">{store.pages().length > 1 ? t('export.scopePage') : t('export.scopeBoard')}</option>
              {selection.length > 0 && <option value="selection">{t('export.scopeSelection')}</option>}
              {sections.map((s) => (
                <option key={s.id} value={`section:${s.id}`}>
                  {t('export.scopeSection', { name: s.name || t('editor.untitledSection') })}
                </option>
              ))}
            </Select>
          </label>
        )}
        <label className="flex flex-col gap-1 font-sans text-sm font-medium text-fg">
          {t('export.format')}
          <Select value={format} onChange={(e) => setFormat(e.target.value as ExportFormat)} disabled={running}>
            {FORMATS.map((f) => (
              <option key={f} value={f}>
                {t(`export.formats.${f}`)}
              </option>
            ))}
          </Select>
        </label>
        {raster && (
          <div className="flex items-center gap-3 font-sans text-sm text-fg">
            <span aria-hidden="true">{t('export.scale')}</span>
            <RadioGroup
              variant="segmented"
              label={t('export.scale')}
              value={String(scale)}
              disabled={running}
              onValueChange={(v) => setScale(Number(v) as 1 | 2 | 4)}
              options={SCALES.map((v) => ({ value: String(v), label: `${v}×` }))}
            />
          </div>
        )}
        {setup.protected && <p className="font-sans text-xs text-fg-muted">{t('export.protectedNote')}</p>}
        {running && (
          <p role="status" className="font-sans text-sm text-fg-muted">
            {progress ? `${progress[0]} / ${progress[1]}` : t('export.running')}
          </p>
        )}
        {outcome === 'failed' && <p role="alert" className="font-sans text-xs text-danger">{t('export.failed')}</p>}
        {outcome === 'empty' && <p role="alert" className="font-sans text-xs text-danger">{t('export.empty')}</p>}
        {outcome && typeof outcome === 'object' && (
          <div className="flex flex-col gap-1 font-sans text-sm text-fg">
            <p>{t('export.done')}</p>
            {outcome.reducedTo !== null && <p>{t('export.reduced', { scale: outcome.reducedTo })}</p>}
            {outcome.missing > 0 && <p>{t('export.missing', { count: outcome.missing })}</p>}
          </div>
        )}
        <div className="flex justify-end gap-2">
          {running ? (
            <Button onClick={() => abort.current?.abort()}>
              {t('export.cancel')}
            </Button>
          ) : (
            <>
              <Button onClick={onClose}>
                {t('export.close')}
              </Button>
              <Button variant="primary" type="submit">
                {t('export.run')}
              </Button>
            </>
          )}
        </div>
      </form>
    </Dialog>
  );
}
